import { buildPresenceConditionName } from 'src/helpers/presence_scene_tokens';
import type { FlowCardDefinition } from '../types';

export type PresenceEvent = 'presence' | 'absence' | 'enter' | 'leave' | 'object_count';

export function presenceRuntimeCondition(data: Record<string, any>) {
	const event = String(data.presenceEvent || 'presence') as PresenceEvent;
	const zoneId = String(data.zoneId || '');
	if (event === 'presence' || event === 'absence') {
		return {
			property: buildPresenceConditionName('st', zoneId),
			operator: 'eq',
			value: event === 'presence' ? 'true' : 'false',
		};
	}
	if (event === 'enter' || event === 'leave') {
		return {
			property: buildPresenceConditionName('nobj_d', zoneId),
			operator: event === 'enter' ? 'gt' : 'lt',
			value: 0,
		};
	}
	return {
		property: buildPresenceConditionName('nobj', zoneId),
		operator: String(data.objectOperator || 'eq'),
		value: Number(data.objectCount ?? 1),
	};
}

export const presenceConditionCard: FlowCardDefinition = {
	type: 'presenceCondition',
	version: '1.0.0',
	domain: 'presence',
	role: 'condition',
	category: 'conditions',
	label: 'Presence',
	description: 'React when a person enters, leaves, is present, or is absent from a zone',
	icon: 'fa-person-rays',
	color: '#06b6d4',
	deviceRequirement: 'presence',
	schema: {
		$schema: 'https://json-schema.org/draft/2020-12/schema',
		type: 'object',
		required: ['device', 'zoneId', 'presenceEvent'],
		properties: {
			device: {
				type: 'object',
				required: ['id'],
				properties: {
					id: { type: 'string', minLength: 1 },
					name: { type: 'string' },
					channel: { type: 'string', default: '0' },
				},
			},
			zoneId: { type: 'string', minLength: 1 },
			zoneName: { type: 'string' },
			presenceEvent: {
				type: 'string',
				enum: ['presence', 'absence', 'enter', 'leave', 'object_count'],
				default: 'presence',
			},
			objectOperator: { type: 'string', enum: ['eq', 'lt', 'gt'], default: 'eq' },
			objectCount: { type: 'integer', minimum: 0, default: 1 },
		},
	},
	fields: [
		{ path: 'device', label: 'Presence sensor', type: 'device', deviceFilter: 'presence' },
		{ path: 'zoneId', label: 'Zone', type: 'presence-zone' },
		{
			path: 'presenceEvent',
			label: 'When',
			type: 'select',
			options: [
				{ value: 'presence', label: 'Someone is present' },
				{ value: 'absence', label: 'Zone is empty' },
				{ value: 'enter', label: 'Someone enters' },
				{ value: 'leave', label: 'Someone leaves' },
				{ value: 'object_count', label: 'Object count matches' },
			],
		},
		{
			path: 'objectOperator',
			label: 'Comparison',
			type: 'select',
			visibleWhen: { path: 'presenceEvent', equals: 'object_count' },
			options: [
				{ value: 'eq', label: 'Exactly' },
				{ value: 'gt', label: 'More than' },
				{ value: 'lt', label: 'Less than' },
			],
		},
		{
			path: 'objectCount',
			label: 'Objects',
			type: 'number',
			min: 0,
			step: 1,
			visibleWhen: { path: 'presenceEvent', equals: 'object_count' },
		},
	],
	ports: { sources: ['true', 'false'] },
	defaults: () => ({
		device: undefined,
		zoneId: '',
		zoneName: '',
		presenceEvent: 'presence',
		objectOperator: 'eq',
		objectCount: 1,
		executionMode: 'any',
		cardVersion: '1.0.0',
	}),
	summary: (data) => {
		const device = data.device?.name || 'presence sensor';
		const zone = data.zoneName || (data.zoneId ? `Zone ${data.zoneId}` : 'a zone');
		const labels: Record<string, string> = {
			presence: `Someone is present in ${zone}`,
			absence: `${zone} is empty`,
			enter: `Someone enters ${zone}`,
			leave: `Someone leaves ${zone}`,
			object_count: `${zone} has ${data.objectOperator || 'eq'} ${data.objectCount ?? 1} objects`,
		};
		return `${device}: ${labels[data.presenceEvent] || 'choose an event'}`;
	},
	validate: (data) => {
		const issues = [];
		if (!data.device?.id) issues.push({ path: 'device', message: 'Select a Presence sensor' });
		if (!String(data.zoneId || '').trim()) issues.push({ path: 'zoneId', message: 'Select a zone' });
		return issues;
	},
	compile: (node, context) => {
		if (!node.data.device?.id || !node.data.zoneId) return null;
		const runtime = presenceRuntimeCondition(node.data);
		const rawId = String(node.data.device.id);
		const parsedId = parseInt(rawId, 16);
		const backendId =
			rawId.toLowerCase().startsWith('x') || Number.isNaN(parsedId) ? rawId : parsedId;
		const ref = `dev:${backendId}:${runtime.property}`;
		const compiled = context?.nested
			? {
					r: ref,
					cmpop: runtime.operator,
					cmpval: runtime.value,
					_gui_purpose: 'condition',
				}
			: {
					[runtime.operator]: [{ rr: ref }, runtime.value],
					_gui_purpose: 'condition',
				};
		return {
			...compiled,
			_flow_card: 'presenceCondition',
			_flow_card_version: '1.0.0',
		};
	},
};
